import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Field, reduxForm, change, getFormValues } from 'redux-form';
import classNames from 'classnames';
import { Button, ButtonToolbar } from 'reactstrap';
import Picker from 'rsuite/DatePicker';
import lodash from 'lodash';
import moment from 'moment';
import PoundIcon from 'mdi-react/PoundIcon';
import CurrencyUsdIcon from 'mdi-react/CurrencyUsdIcon';
import renderSelectField from '../../../../shared/components/form/Select';
import renderCheckBoxField from '../../../../shared/components/form/CheckBox';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { AD_TYPE_CSV_OPTIONS, AD_TYPE_GROUPS } from '../../shared/constants';
import config from '../../../../config';

const renderFieldForm = ({
  input, placeholder, type, isDisabled, meta: { touched, error },
}) => (
  <div className="form__form-group-input-wrap">
    <input {...input} placeholder={placeholder} type={type} disabled={isDisabled} />
    {touched && error && <span className="form__form-group-error">{error}</span>}
  </div>
);

renderFieldForm.propTypes = {
  input: PropTypes.shape().isRequired,
  placeholder: PropTypes.string,
  type: PropTypes.string,
  isDisabled: PropTypes.string,
  value: PropTypes.string,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
};

renderFieldForm.defaultProps = {
  placeholder: '',
  meta: null,
  type: 'text',
  isDisabled: '',
};

const renderTextArea = ({ input, meta: { touched, error } }) => (
  <div style={{ width: '100%' }}>
    <label htmlFor="account_settings_form" style={{ width: '100%' }}>
      <div style={{ width: '100%' }}>
        <textarea
          {...input}
          id="account_settings_form"
          rows="10"
          style={{
            width: '100%',
          }}
        />
        {touched && error && <span className="form__form-group-error">{error}</span>}
      </div>
    </label>
  </div>
);

const RenderDatePicker = ({
  input, handleDateChange, meta: { touched, error },
}) => {
  const [pickerId] = useState(lodash.uniqueId('date-picker-'));
  const theme = useSelector(store => store.theme);
  const pickerClasses = classNames(theme.className, theme.className.endsWith('light') ? 'rs-theme-light' : 'rs-theme-dark');
  return (
    <div id={pickerId} style={{ position: 'absolute' }} className={pickerClasses}>
      <Picker
        value={input.value ? new Date(input.value) : null}
        onChange={handleDateChange}
        container={document.getElementById(pickerId)}
        format="yyyy-MM-dd"
        oneTap
      />
      {touched && error && <span className="form__form-group-error">{error}</span>}
    </div>
  )
};

RenderDatePicker.propTypes = {
  input: PropTypes.shape().isRequired,
  handleDateChange: PropTypes.func.isRequired,
};

const ItemEditModalForm = ({
  handleSubmit, currentEditRow, changeShowEditModal, activeDayOptions, monthOptions, user, budgetAccountData, disableButton, setShowEditModal, retailerConfig,
}) => {
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);

  const stateOptions = [
    { label: 'Enabled', value: 'enabled' },
    { label: 'Disabled', value: 'disabled' },
    { label: 'Draft', value: 'draft' },
    { label: 'Completed', value: 'completed' },
  ];

  const budgetTypeOptions = [
    { label: 'Item', value: 'item' },
    { label: 'Brand', value: 'brand' },
  ];

  const brandAdFormatOptions = [
    { label: 'Product Collection', value: 'product_collection' },
    { label: 'Store Spotlight', value: 'store_spotlight' },
  ];

  const setupFlowOptions = [
    { label: 'Quick Setup', value: 'quick' },
    { label: 'Advanced Setup', value: 'advanced' },
  ];

  const agencyInternalOptions = [
    { label: 'Agency View', value: false },
    { label: 'Limitless View', value: true },
  ];

  const budgetDriverOptions = [
    { label: 'Daily', value: 'daily' },
    { label: 'Monthly', value: 'monthly' },
  ];

  const limitlessBudgetOptions = [
    { label: 'Strict Budget', value: 0 },
    { label: 'No Budget', value: 1 },
    { label: 'Flexible Budget', value: 2 },
  ];

  const includesOptions = [
    { label: 'All ADs', value: '' },
    { label: 'Limitless ADs', value: 'limitless' },
  ];

  const spendPriorityOptions = [
    { label: 'Most Brand', value: '-2' },
    { label: 'More Brand', value: '-1' },
    { label: 'Brand and Non-Brand', value: '0' },
    { label: 'More NonBrand', value: '1' },
    { label: 'Most NonBrand', value: '2' },
  ];

  const spendTacticOptions = [
    { label: 'Max Performance', value: 'max_perf' },
    { label: 'Full Budget Spend', value: 'full_spend' },
  ];

  const spendSpeedOptions = [
    { label: 'Standard', value: 'speed_level_1' },
    { label: 'Fast', value: 'speed_level_2' },
    { label: 'Fastest', value: 'speed_level_3' },
  ];

  const itemTypeOptions = [
    { label: 'SKU', value: 'SKU' },
    { label: 'ASIN', value: 'ASIN' },
    { label: 'ITEM', value: 'ITEM' },
  ];

  const itemSelectionOptions = [
    { label: 'All', value: 'ALL' },
    { label: 'Top', value: 'TOP' },
  ];

  const destinationOptions = [
    { label: 'On', value: 'ON' },
    { label: 'Off', value: 'OFF' },
  ];

  const costTargetTypeOptions = [
    { label: 'Blended', value: 'blended' },
    { label: 'Brand/NonBrand', value: 'bnb' },
  ];

  function cleanNumber(value) {
    if (value) {
      return value.toString().match(/^[0-9]*(?:\.\d{0,2})?/)[0];
    }
    return value;
  }

  function initFormatTwoPlaces(value) {
    if (value) {
      return (((value || 0) * 100) / 100).toFixed(2);
    }
    return value;
  }

  function itemCsvInput(value) {
    if (value) {
      return value
        .toString()
        .trim() // remove leading/trailing whitespace
        .replace(/\r/g, '') // remove carriage returns
        .replace(/\s*,\s*/g, ',') // remove space around commas
        .replace(/\n+/g, ',');
    }
    return value;
  }

  const selectedSeller = useSelector(store => store.sellerSelect);
  const formValues = useSelector(state => getFormValues('budgetGroupEditModal')(state));
  const { setupFlow, agencyInternal, budgetDriver, costTargetType, budgetAllocationSpSd, budgetAllocationSb, budgetAllocationSt, masterItemOnList, masterItemOffList, budgetType } = formValues || {};

  const dispatch = useDispatch();
  const initDropdown = (name, value, options, position = 0) => {
    dispatch(change('budgetGroupEditModal', name, value || value === '' ? options.find(option => option.value === value) : options[position]));
  };

  // When budgetType changes to 'brand', clear portfolio and set item_type to ASIN
  useEffect(() => {
    if (budgetType?.value === 'brand') {
      // Clear portfolio ID for brand budgets
      dispatch(change('budgetGroupEditModal', 'portfolioIdsCsv', ''));
      // Set item type to ASIN for brand budgets
      const asinOption = itemTypeOptions.find(opt => opt.value === 'ASIN');
      if (asinOption) {
        dispatch(change('budgetGroupEditModal', 'itemType', asinOption));
      }
    }
  }, [budgetType, dispatch]);

  // Fetch brands from API when budgetType changes to 'brand'
  useEffect(() => {
    if (budgetType?.value === 'brand' && selectedSeller.value && brands.length === 0 && !loadingBrands) {
      setLoadingBrands(true);
      const fetchUrl = `${config.serverUrl}/customers/brands?accountId=${selectedSeller.value}`;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

      fetch(fetchUrl, { headers: headerWithAuth })
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to fetch brands');
          }
          return response.json();
        })
        .then(data => {
          const brandOptions = data.map(brand => ({
            label: brand.brandRegistryName,
            value: brand.brandId,
            brandEntityId: brand.brandEntityId,
          }));
          setBrands(brandOptions);
          setLoadingBrands(false);

          // If editing an existing brand budget, set the brand dropdown value
          if (currentEditRow) {
            const currentBudgetTypeForBrand = currentEditRow.budget_type || currentEditRow.budget_item_type;
            if (currentBudgetTypeForBrand === 'brand' && currentEditRow.brand_entity_id) {
              const matchingBrand = brandOptions.find(b => b.brandEntityId === currentEditRow.brand_entity_id);
              if (matchingBrand) {
                dispatch(change('budgetGroupEditModal', 'brand', matchingBrand));
              }
            }
          }
        })
        .catch(error => {
          console.error('Error fetching brands:', error);
          setLoadingBrands(false);
        });
    }
  }, [budgetType, selectedSeller.value, brands.length, loadingBrands, user.jwtToken, currentEditRow, dispatch]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!currentEditRow) return;

    const ctb = parseFloat(currentEditRow.cost_target_brand || 0);
    const ctnb = parseFloat(currentEditRow.cost_target_nonbrand || 0);
    const costTarget = {
      ctb,
      ctnb,
    }
    if (currentEditRow.cost_target_metric === 'pacos') {
      if (ctb > 0) {
        costTarget.ctb = (1 / ctb) * 100;
      }
      if (ctnb > 0) {
        costTarget.ctnb = (1 / ctnb) * 100;
      }
    }

    dispatch(change('budgetGroupEditModal', 'monthOptions', monthOptions));
    dispatch(change('budgetGroupEditModal', 'accountType', selectedSeller.type));
    dispatch(change('budgetGroupEditModal', 'retailerKey', retailerConfig?.key || 'amazon'));

    // Initialize budget type (default to 'item')
    // Check both budget_type (new) and budget_item_type (legacy) for backward compatibility
    initDropdown('budgetType', currentEditRow.budget_type || currentEditRow.budget_item_type, budgetTypeOptions, 0);

    // Initialize brand-related fields if budget_type or budget_item_type is 'brand'
    const currentBudgetType = currentEditRow.budget_type || currentEditRow.budget_item_type;
    if (currentBudgetType === 'brand') {
      dispatch(change('budgetGroupEditModal', 'brandName', currentEditRow.brand_name || ''));
      dispatch(change('budgetGroupEditModal', 'brandEntityId', currentEditRow.brand_entity_id || ''));
      dispatch(change('budgetGroupEditModal', 'brandAdFormat', currentEditRow.ad_format ? brandAdFormatOptions.find(opt => opt.value === currentEditRow.ad_format) : brandAdFormatOptions[0]));

      // Set up the brand dropdown value if we have brand info
      if (currentEditRow.brand_name && currentEditRow.brand_entity_id) {
        // We'll need to wait for brands to load, or initialize with current value
        const currentBrand = {
          label: currentEditRow.brand_name,
          value: currentEditRow.brand_id || currentEditRow.brand_entity_id,
          brandEntityId: currentEditRow.brand_entity_id,
        };
        dispatch(change('budgetGroupEditModal', 'brand', currentBrand));
      }
    }

    // Set budgetGroupRecords from currentEditRow
    if (currentEditRow.budgetGroupRecords) {
      dispatch(change('budgetGroupEditModal', 'budgetGroupRecords', currentEditRow.budgetGroupRecords));
    }

    dispatch(change('budgetGroupEditModal', 'budgetGroupName', currentEditRow.budget_group_name));

    // Initialize the three master item fields from new API structure
    dispatch(change('budgetGroupEditModal', 'masterItemOrigList', currentEditRow.master_item_orig_csv || currentEditRow.master_item_csv || ''));
    dispatch(change('budgetGroupEditModal', 'masterItemOnList', currentEditRow.master_item_on_csv || currentEditRow.master_item_csv || ''));
    dispatch(change('budgetGroupEditModal', 'masterItemOffList', currentEditRow.master_item_off_csv || ''));
    dispatch(change('budgetGroupEditModal', 'setupFlow', currentEditRow.form_type ? setupFlowOptions.find(option => option.value === currentEditRow.form_type) : setupFlowOptions[0]));
    dispatch(change('budgetGroupEditModal', 'agencyInternal', agencyInternalOptions[0]));
    if (currentEditRow.active_days_csv) {
      const activeDays = currentEditRow.active_days_csv.split(',');
      activeDays.forEach((day, pos) => {
        const activeDayObj = activeDayOptions.find(dayObj => dayObj.value === pos.toString());
        dispatch(change('budgetGroupEditModal', activeDayObj.name, day === '1'));
      });
    }
    initDropdown('adTypeCsv', currentEditRow.ad_type_csv, AD_TYPE_CSV_OPTIONS, 0);

    // Only auto-populate portfolio for Item budgets, not Brand budgets
    if (currentBudgetType !== 'brand') {
      dispatch(change('budgetGroupEditModal', 'portfolioIdsCsv', currentEditRow?.portfolio_ids_csv === '' ? '' : budgetAccountData?.portfolio_id));
    } else {
      dispatch(change('budgetGroupEditModal', 'portfolioIdsCsv', currentEditRow?.portfolio_ids_csv || ''));
    }

    initDropdown('includes', currentEditRow.includes, includesOptions, 1);
    dispatch(change('budgetGroupEditModal', 'costTargetType', currentEditRow.cost_target_type));
    dispatch(change('budgetGroupEditModal', 'costTargetMetric', 'roas')); // Always ROAS
    dispatch(change('budgetGroupEditModal', 'costTarget', initFormatTwoPlaces((costTarget.ctb + costTarget.ctnb) / 2)));
    dispatch(change('budgetGroupEditModal', 'costTargetBrand', initFormatTwoPlaces(costTarget.ctb)));
    dispatch(change('budgetGroupEditModal', 'costTargetNonBrand', initFormatTwoPlaces(costTarget.ctnb)));
    initDropdown('spendSpeed', currentEditRow.spend_speed, spendSpeedOptions, 0);
    initDropdown('spendTactic', currentEditRow.spend_tactic, spendTacticOptions, 0);
    initDropdown('limitlessBudget', currentEditRow.limitless_budget, limitlessBudgetOptions, 0);
    initDropdown('spendPriority', currentEditRow.spend_priority, spendPriorityOptions, 2);

    // Initialize new fields: item_type, item_selection, destination
    // item_type logic:
    // - For Brand budgets on Amazon, always use ASIN
    // - For Item budgets on Amazon, use seller_account_type_id (1=SKU, 2=ASIN)
    // - For Walmart, use ITEM
    let defaultItemType = currentEditRow.item_type;
    if (!defaultItemType) {
      if (currentBudgetType === 'brand' && retailerConfig?.key === 'amazon') {
        defaultItemType = 'ASIN';
      } else if (retailerConfig?.key === 'amazon') {
        defaultItemType = selectedSeller.type === 1 ? 'SKU' : 'ASIN';
      } else if (retailerConfig?.key === 'walmart') {
        defaultItemType = 'ITEM';
      }
    }
    initDropdown('itemType', defaultItemType, itemTypeOptions, 0);
    initDropdown('itemSelection', currentEditRow.item_selection, itemSelectionOptions, 0);
    initDropdown('destination', currentEditRow.destination, destinationOptions, 0);
    dispatch(change('budgetGroupEditModal', 'startDate', currentEditRow.start_date ? moment(currentEditRow.start_date) : moment().add(1, 'day')));
    dispatch(change('budgetGroupEditModal', 'endDate', currentEditRow.end_date ? moment(currentEditRow.end_date) : null));
    initDropdown('budgetDriver', currentEditRow.budget_driver, budgetDriverOptions, 0);
    initDropdown('costTargetType', currentEditRow.cost_target_type, costTargetTypeOptions, 0);
    if (currentEditRow.budget_json) {
      const budgetJson = JSON.parse(currentEditRow.budget_json);

      if (budgetJson.monthly) {
        Object.keys(budgetJson.monthly).forEach((monthNum) => {
          const monthName = budgetJson.monthly[monthNum].name.toLowerCase();
          // Try allocation.original.monthly[monthName] first (new nested structure)
          // Then try allocation[`original_${monthName}`] (old flat structure)
          // Finally fall back to monthly[monthNum].budget (legacy)
          let monthBudget = 0;
          if (budgetJson.allocation?.original?.monthly?.[monthName] !== undefined) {
            monthBudget = budgetJson.allocation.original.monthly[monthName];
          } else if (budgetJson.allocation?.[`original_${monthName}`] !== undefined) {
            monthBudget = budgetJson.allocation[`original_${monthName}`];
          } else if (budgetJson.monthly[monthNum]?.budget !== undefined) {
            monthBudget = budgetJson.monthly[monthNum].budget;
          }
          dispatch(change('budgetGroupEditModal', monthName, initFormatTwoPlaces(monthBudget)));
        });
      }
      if (budgetJson.daily) {
        // Try allocation.original.daily.budget first (new nested structure)
        // Then try allocation.original_daily_budget (old flat structure)
        // Finally fall back to daily.budget (legacy)
        let dailyBudget = 0;
        let reallocation = 0;

        if (budgetJson.allocation?.original?.daily?.budget !== undefined) {
          dailyBudget = budgetJson.allocation.original.daily.budget;
        } else if (budgetJson.allocation?.original_daily_budget !== undefined) {
          dailyBudget = budgetJson.allocation.original_daily_budget;
        } else if (budgetJson.daily?.budget !== undefined) {
          dailyBudget = budgetJson.daily.budget;
        }

        if (budgetJson.allocation?.original?.daily?.reallocation !== undefined) {
          reallocation = budgetJson.allocation.original.daily.reallocation;
        } else if (budgetJson.allocation?.original_reallocation !== undefined) {
          reallocation = budgetJson.allocation.original_reallocation;
        } else if (budgetJson.daily?.reallocation !== undefined) {
          reallocation = budgetJson.daily.reallocation;
        }

        dispatch(change('budgetGroupEditModal', 'baseDailyBudgetAmount', initFormatTwoPlaces(dailyBudget)));
        dispatch(change('budgetGroupEditModal', 'reallocation', initFormatTwoPlaces(reallocation)));
      }
      // Load budget allocation percentages if they exist (from new nested structure)
      if (budgetJson.allocation && budgetJson.allocation.percentages) {
        dispatch(change('budgetGroupEditModal', 'budgetAllocationSpSd', budgetJson.allocation.percentages.sp_sd_percent || ''));
        dispatch(change('budgetGroupEditModal', 'budgetAllocationSb', budgetJson.allocation.percentages.sb_percent || ''));
        dispatch(change('budgetGroupEditModal', 'budgetAllocationSt', budgetJson.allocation.percentages.st_percent || ''));
      } else {
        // Default to 100% SP+SD for new groups or groups without allocation data
        dispatch(change('budgetGroupEditModal', 'budgetAllocationSpSd', 100));
        dispatch(change('budgetGroupEditModal', 'budgetAllocationSb', 0));
        dispatch(change('budgetGroupEditModal', 'budgetAllocationSt', 0));
      }
    } else {
      // No budget_json at all - default to 100% SP+SD for new budget groups
      dispatch(change('budgetGroupEditModal', 'budgetAllocationSpSd', 100));
      dispatch(change('budgetGroupEditModal', 'budgetAllocationSb', 0));
      dispatch(change('budgetGroupEditModal', 'budgetAllocationSt', 0));
    }
    dispatch(change('budgetGroupEditModal', 'state', currentEditRow.state ? stateOptions.find(option => option.value === currentEditRow.state) : stateOptions[0]));
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (currentEditRow && currentEditRow.active_days_csv) {
      const activeDays = currentEditRow.active_days_csv.split(',');
      activeDays.forEach((day, pos) => {
        const activeDayObj = activeDayOptions.find(dayObj => dayObj.value === pos.toString());
        dispatch(change('budgetGroupEditModal', activeDayObj.name, day === '1'));
      });
    } else {
      activeDayOptions.forEach((day) => {
        dispatch(change('budgetGroupEditModal', day.name, true));
      });
    }
  }, [setupFlow]);

  return (
    <>
      {budgetAccountData.cost_metric_view && (
        <form className="form" onSubmit={handleSubmit}>
          <h2>Manage Budget Groups</h2>
          <div className="form__form-group">
            <br />
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label typography-message">Budget Group Name <Tooltip id="budgetGroupName" msg="Case ensitive and must be unique" placement="top" /></span>
            <div className="form__form-group-field">
              <Field
                name="budgetGroupName"
                type="text"
                component={renderFieldForm}
                required
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">Budget Group Setup Flow</span>
            <div className="form__form-group-field">
              <Field
                name="setupFlow"
                type="text"
                component={renderSelectField}
                options={setupFlowOptions}
              />
            </div>
          </div>
          {user.access === 'admin' && (
            <div className="form__form-group">
              <span className="form__form-group-label">View Toggle</span>
              <div className="form__form-group-field">
                <Field
                  name="agencyInternal"
                  type="text"
                  component={renderSelectField}
                  options={agencyInternalOptions}
                  defaultValue={agencyInternal}
                />
              </div>
            </div>
          )}
          {(setupFlow?.value || 'advanced') === 'advanced' && (
            <div className="form__form-group">
              <span className="form__form-group-label">Allowed Days of the Week</span>
              {activeDayOptions && activeDayOptions.map((day) => (
                <div className="form__form-group-field">
                  <Field
                    name={day.name}
                    component={renderCheckBoxField}
                    label={day.label}
                  />
                </div>
              ))}
            </div>
          )}
          {setupFlow?.value === 'advanced' && agencyInternal?.value && retailerConfig?.key === 'amazon' && (
            <div className="form__form-group">
              <span className="form__form-group-label typography-message">Portfolio(s)</span>
              <div className="form__form-group-field">
                <Field
                  name="portfolioIdsCsv"
                  type="text"
                  component={renderFieldForm}
                />
              </div>
            </div>
          )}
          {setupFlow?.value === 'advanced' && agencyInternal?.value && (
            <div className="form__form-group">
              <span className="form__form-group-label">Includes</span>
              <div className="form__form-group-field">
                <Field
                  name="includes"
                  type="text"
                  component={renderSelectField}
                  options={includesOptions}
                />
              </div>
            </div>
          )}
          <div className="form__form-group">
            <span className="form__form-group-label">Cost Target Type</span>
            <div className="form__form-group-field">
              <Field
                name="costTargetType"
                type="text"
                component={renderSelectField}
                options={costTargetTypeOptions}
              />
            </div>
          </div>
          {costTargetType?.value === 'blended' && (
            <div className="form__form-group">
              <span className="form__form-group-label typography-message">Cost Target (ROAS)</span>
              <div className="form__form-group-field">
                <div className="form__form-group-icon">
                  <PoundIcon />
                </div>
                <Field
                  name="costTarget"
                  type="text"
                  component={renderFieldForm}
                  // parse={cleanNumber}
                />
              </div>
              {formValues.costTarget && !Number.isNaN(parseFloat(formValues.costTarget)) && parseFloat(formValues.costTarget) > 0 && (
                <span className="form__form-group-label typography-message" style={{ fontSize: '0.9em', color: '#70bbfd', marginTop: '5px' }}>
                  Estimated PACoS: {((1 / parseFloat(formValues.costTarget)) * 100).toFixed(2)}%
                </span>
              )}
            </div>
          )}
          {costTargetType?.value === 'bnb' && (
            <div className="form__form-group">
              <span className="form__form-group-label">Brand Cost Target (ROAS)</span>
              <div className="form__form-group-field">
                <div className="form__form-group-icon">
                  <PoundIcon />
                </div>
                <Field
                  name="costTargetBrand"
                  type="text"
                  component={renderFieldForm}
                  format={cleanNumber}
                />
              </div>
              {formValues.costTargetBrand && !Number.isNaN(parseFloat(formValues.costTargetBrand)) && parseFloat(formValues.costTargetBrand) > 0 && (
                <span className="form__form-group-label typography-message" style={{ fontSize: '0.9em', color: '#70bbfd', marginTop: '5px' }}>
                  Estimated PACoS: {((1 / parseFloat(formValues.costTargetBrand)) * 100).toFixed(2)}%
                </span>
              )}
            </div>
          )}
          {costTargetType?.value === 'bnb' && (
            <div className="form__form-group">
              <span className="form__form-group-label">Non-Brand Cost Target (ROAS)</span>
              <div className="form__form-group-field">
                <div className="form__form-group-icon">
                  <PoundIcon />
                </div>
                <Field
                  name="costTargetNonBrand"
                  type="text"
                  component={renderFieldForm}
                  format={cleanNumber}
                />
              </div>
              {formValues.costTargetNonBrand && !Number.isNaN(parseFloat(formValues.costTargetNonBrand)) && parseFloat(formValues.costTargetNonBrand) > 0 && (
                <span className="form__form-group-label typography-message" style={{ fontSize: '0.9em', color: '#70bbfd', marginTop: '5px' }}>
                  Estimated PACoS: {((1 / parseFloat(formValues.costTargetNonBrand)) * 100).toFixed(2)}%
                </span>
              )}
            </div>
          )}
          {setupFlow?.value === 'advanced' && agencyInternal?.value && (
            <div className="form__form-group">
              <span className="form__form-group-label">Spend Speed</span>
              <div className="form__form-group-field">
                <Field
                  name="spendSpeed"
                  type="text"
                  component={renderSelectField}
                  options={spendSpeedOptions}
                />
              </div>
            </div>
          )}
          {setupFlow?.value === 'advanced' && agencyInternal?.value && (
            <div className="form__form-group">
              <span className="form__form-group-label">Spend Tactic</span>
              <div className="form__form-group-field">
                <Field
                  name="spendTactic"
                  type="text"
                  component={renderSelectField}
                  options={spendTacticOptions}
                />
              </div>
            </div>
          )}
          {setupFlow?.value === 'advanced' && agencyInternal?.value && (
            <div className="form__form-group">
              <span className="form__form-group-label">Limitless Budget</span>
              <div className="form__form-group-field">
                <Field
                  name="limitlessBudget"
                  type="text"
                  component={renderSelectField}
                  options={limitlessBudgetOptions}
                />
              </div>
            </div>
          )}
          <div className="form__form-group">
            <span className="form__form-group-label">Spend Priority</span>
            <div className="form__form-group-field">
              <Field
                name="spendPriority"
                type="text"
                component={renderSelectField}
                options={spendPriorityOptions}
              />
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">Budget Type <Tooltip id="budgetType" msg="Choose Item for product-based budgets or Brand for brand-based budgets" placement="top" /></span>
            <div className="form__form-group-field">
              <Field
                name="budgetType"
                type="text"
                component={renderSelectField}
                options={budgetTypeOptions}
              />
            </div>
          </div>
          {budgetType?.value === 'brand' && (
            <>
              <div className="form__form-group">
                <span className="form__form-group-label">Brand <Tooltip id="brand" msg="Select the brand for this budget group" placement="top" /></span>
                <div className="form__form-group-field">
                  {loadingBrands ? (
                    <div>Loading brands...</div>
                  ) : (
                    <Field
                      name="brand"
                      type="text"
                      component={renderSelectField}
                      options={brands}
                      onChange={(value) => {
                        if (value) {
                          dispatch(change('budgetGroupEditModal', 'brandName', value.label));
                          dispatch(change('budgetGroupEditModal', 'brandEntityId', value.brandEntityId));
                        }
                      }}
                    />
                  )}
                </div>
              </div>
              <div className="form__form-group">
                <span className="form__form-group-label">Ad Format <Tooltip id="brandAdFormat" msg="Select the ad format for this brand budget" placement="top" /></span>
                <div className="form__form-group-field">
                  <Field
                    name="brandAdFormat"
                    type="text"
                    component={renderSelectField}
                    options={brandAdFormatOptions}
                  />
                </div>
              </div>
            </>
          )}
          {setupFlow?.value === 'advanced' && agencyInternal?.value && (
            <div className="form__form-group">
              <span className="form__form-group-label">Item Type</span>
              <div className="form__form-group-field">
                <Field
                  name="itemType"
                  type="text"
                  component={renderSelectField}
                  options={itemTypeOptions}
                />
              </div>
            </div>
          )}
          {setupFlow?.value === 'advanced' && agencyInternal?.value && (
            <div className="form__form-group">
              <span className="form__form-group-label">Item Selection</span>
              <div className="form__form-group-field">
                <Field
                  name="itemSelection"
                  type="text"
                  component={renderSelectField}
                  options={itemSelectionOptions}
                />
              </div>
            </div>
          )}
          {setupFlow?.value === 'advanced' && agencyInternal?.value && (
            <div className="form__form-group">
              <span className="form__form-group-label">Destination <Tooltip id="destination" msg="Describes where we are going to take ad traffic (ON or OFF)" placement="top" /></span>
              <div className="form__form-group-field">
                <Field
                  name="destination"
                  type="text"
                  component={renderSelectField}
                  options={destinationOptions}
                />
              </div>
            </div>
          )}
          <div className="form__form-group">
            <span className="form__form-group-label">
              {budgetType?.value === 'brand' ? (
                <>
                  Allowed ASINs (<a href="/self-service/budget#Items" target="_blank" rel="noopener noreferrer">View Catalog HERE</a>)
                  {' '}<Tooltip id="masterItemOnList" msg="List of ASINs allowed for this brand budget (3-100 required)" placement="top" />
                </>
              ) : (
                <>
                  ON&nbsp;
                  {(() => {
                    if (retailerConfig?.key === 'amazon') {
                      return selectedSeller.type === 1 ? 'SKUs' : 'ASINs';
                    }
                    return 'Items';
                  })()}
                  &nbsp;(<a href="/self-service/budget#Items" target="_blank" rel="noopener noreferrer">View Catalog HERE</a>)&nbsp;
                  <Tooltip id="masterItemOnList" msg="Items in this list will have their Ad Status turned ON" placement="top" />
                </>
              )}
            </span>
            <div className="form__form-group-field">
              <Field
                name="masterItemOnList"
                type="text"
                component={renderTextArea}
                format={itemCsvInput}
              />
            </div>
            {masterItemOnList && (() => {
              const itemList = (masterItemOnList || '').split(',').map(item => item.trim()).filter(item => item);
              const itemCount = itemList.length;
              const upperCaseItems = itemList.map(item => item.toUpperCase());
              const hasDuplicates = new Set(upperCaseItems).size !== itemList.length;

              if (budgetType?.value === 'brand') {
                const invalidAsins = itemList.filter(item => !item?.toUpperCase().startsWith('B0') || item?.length !== 10);
                const isCountValid = itemCount >= 3 && itemCount <= 100;
                const isFormatValid = invalidAsins.length === 0;
                const isValid = isCountValid && isFormatValid && !hasDuplicates;

                return (
                  <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', border: `1px solid ${isValid ? 'rgba(70, 187, 253, 0.4)' : 'rgba(255, 0, 0, 0.4)'}`, backgroundColor: `${isValid ? 'rgba(70, 187, 253, 0.1)' : 'rgba(255, 0, 0, 0.1)'}` }}>
                    <div style={{ fontWeight: '500', color: isValid ? '#70bbfd' : '#ff4861' }}>
                      {isValid ? '✓' : '⚠'} ASIN Count: {itemCount}
                    </div>
                    {!isCountValid && (
                      <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                        Brand budgets require 3-100 ASINs
                      </div>
                    )}
                    {invalidAsins.length > 0 && (
                      <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                        Invalid ASINs (must start with B0 and be 10 characters): {invalidAsins.join(', ')}
                      </div>
                    )}
                    {hasDuplicates && (
                      <div style={{ fontSize: '0.9em', color: '#721c24', marginTop: '4px' }}>
                        ⚠ Duplicate ASINs found in list
                      </div>
                    )}
                    {isValid && (
                      <div style={{ fontSize: '0.9em', color: '#4a5568', marginTop: '4px' }}>
                        All ASINs are valid ✓
                      </div>
                    )}
                  </div>
                );
              }
              // For Item budget type, show duplicate warning if any
              if (hasDuplicates && itemCount > 0) {
                return (
                  <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', border: '1px solid rgba(255, 165, 0, 0.4)', backgroundColor: 'rgba(255, 165, 0, 0.1)' }}>
                    <div style={{ fontWeight: '500', color: '#ff8c00' }}>
                      ⚠ Duplicate items found in list
                    </div>
                  </div>
                );
              }
              return null;
            })()}
          </div>
          {budgetType?.value !== 'brand' && (
            <div className="form__form-group">
              <span className="form__form-group-label">
                OFF&nbsp;
                {(() => {
                  if (retailerConfig?.key === 'amazon') {
                    return selectedSeller.type === 1 ? 'SKUs' : 'ASINs';
                  }
                  return 'Items';
                })()}
                {' '}<Tooltip id="masterItemOffList" msg="Items in this list will have their Ad Status turned OFF" placement="top" />
              </span>
              <div className="form__form-group-field">
                <Field
                  name="masterItemOffList"
                  type="text"
                  component={renderTextArea}
                  format={itemCsvInput}
                />
              </div>
              {masterItemOnList && masterItemOffList && (() => {
                const onItems = (masterItemOnList || '').split(',').map(item => item.trim().toUpperCase()).filter(item => item);
                const offItems = (masterItemOffList || '').split(',').map(item => item.trim().toUpperCase()).filter(item => item);
                const duplicates = onItems.filter(item => offItems.includes(item));
                return duplicates.length > 0 ? (
                  <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', border: '1px solid rgba(255, 0, 0, 0.4)', backgroundColor: 'rgba(255, 0, 0, 0.1)' }}>
                    <div style={{ marginBottom: '5px', fontWeight: '500', color: '#ff4861' }}>❌ Duplicate Items Found:</div>
                    <div style={{ fontSize: '0.9em', color: '#721c24' }}>
                      {duplicates.join(', ')}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}
          {setupFlow?.value === 'advanced' && budgetType?.value !== 'brand' && !(currentEditRow && currentEditRow.budget_group_daily_id_csv) && (
            <div className="form__form-group">
              <span className="form__form-group-label">Create A Budget Group for each Item listed (Advanced)</span>
              <div className="form__form-group-field">
                <Field
                  name="splitItemsCheckbox"
                  component={renderCheckBoxField}
                  label="Split Items Into Individual Budget Groups"
                />
              </div>
            </div>
          )}
          <div className="form__form-group">
            <div style={{ width: '50%', height: '60px', float: 'left' }}>
              <span className="form__form-group-label">Start Date</span>
              <div>
                <Field
                  name="startDate"
                  type="text"
                  // setDate={startDate}
                  handleDateChange={(newDate) => dispatch(change('budgetGroupEditModal', 'startDate', newDate ? moment(newDate) : null))}
                  component={RenderDatePicker}
                  required
                />
              </div>
            </div>
            <div style={{ marginLeft: '50%', height: '60px' }}>
              <span className="form__form-group-label">End Date</span>
              <div className="form__form-group-field">
                <Field
                  name="endDate"
                  type="text"
                  // setDate={formValues.endDate}
                  handleDateChange={(newDate) => dispatch(change('budgetGroupEditModal', 'endDate', newDate ? moment(newDate) : null))}
                  component={RenderDatePicker}
                />
              </div>
            </div>
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">Budget Driver</span>
            <div className="form__form-group-field">
              <Field
                name="budgetDriver"
                type="text"
                component={renderSelectField}
                options={budgetDriverOptions}
              />
            </div>
          </div>
          {budgetType?.value !== 'brand' && (
            <div className="form__form-group">
              <span className="form__form-group-label typography-message">Budget Allocation by Ad Type <Tooltip id="budgetAllocation" msg="Allocate budget percentages across ad types. Total must equal 100%." placement="top" /></span>
              <div className="form__form-group-field">
                <div style={{ display: 'block', marginBottom: '10px', padding: '10px', borderRadius: '4px', border: '1px solid rgba(128, 128, 128, 0.2)' }}>
                  {AD_TYPE_GROUPS.map((adType) => {
                    let fieldName = 'budgetAllocationSt';
                    if (adType.key === 'sp_sd') {
                      fieldName = 'budgetAllocationSpSd';
                    } else if (adType.key === 'sb') {
                      fieldName = 'budgetAllocationSb';
                    }
                    return (
                      <div key={adType.key} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', clear: 'both' }}>
                        <span style={{ width: '120px', fontWeight: '500' }}>{adType.label}:</span>
                        <div style={{ width: '80px', marginRight: '10px' }}>
                          <Field
                            name={fieldName}
                            type="text"
                            component={renderFieldForm}
                            placeholder="0"
                            format={cleanNumber}
                          />
                        </div>
                        <span>%</span>
                      </div>
                    );
                  })}
                  {(budgetAllocationSpSd || budgetAllocationSb || budgetAllocationSt) && (
                    <div style={{ marginTop: '10px', padding: '8px', borderRadius: '4px', border: '1px solid rgba(70, 187, 253, 0.3)', backgroundColor: 'rgba(70, 187, 253, 0.1)' }}>
                      <strong>Total: {((parseFloat(budgetAllocationSpSd) || 0) + (parseFloat(budgetAllocationSb) || 0) + (parseFloat(budgetAllocationSt) || 0)).toFixed(2)}%</strong>
                      {((parseFloat(budgetAllocationSpSd) || 0) + (parseFloat(budgetAllocationSb) || 0) + (parseFloat(budgetAllocationSt) || 0)) !== 100 && (
                        <span style={{ color: '#ff4861', marginLeft: '10px' }}>⚠ Must equal 100%</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {budgetDriver?.value === 'daily' && (
            <div className="form__form-group">
              <span className="form__form-group-label">Total Daily Budget Amount in {selectedSeller.currencyCode} for all Items</span>
              <div className="form__form-group-field">
                <div className="form__form-group-icon">
                  <CurrencyUsdIcon />
                </div>
                <Field
                  name="baseDailyBudgetAmount"
                  type="text"
                  component={renderFieldForm}
                  format={cleanNumber}
                />
              </div>
            </div>
          )}
          {budgetDriver?.value === 'monthly' && (
            <div className="form__form-group">
              <span className="form__form-group-label">Monthly Budget Amount in {selectedSeller.currencyCode}</span>
              {monthOptions && monthOptions.map((month) => (
                <div className="form__form-group-field" style={{ padding: '2px' }}>
                  <span style={{ paddingRight: '10px', paddingTop: '5px', width: '25%', textAlign: 'right' }}>
                    {month.label}:
                  </span>
                  <div className="form__form-group-icon">
                    <CurrencyUsdIcon />
                  </div>
                  <Field
                    name={month.name}
                    type="text"
                    component={renderFieldForm}
                    format={cleanNumber}
                  />
                </div>
              ))}
            </div>
          )}
          {budgetDriver?.value === 'daily' && setupFlow?.value === 'advanced' && agencyInternal?.value && (
            <div className="form__form-group">
              <span className="form__form-group-label">Total Daily Reallocation Budget Amount in {selectedSeller.currencyCode}</span>
              <div className="form__form-group-field">
                <div className="form__form-group-icon">
                  <CurrencyUsdIcon />
                </div>
                <Field
                  name="reallocation"
                  type="text"
                  component={renderFieldForm}
                  format={cleanNumber}
                />
              </div>
            </div>
          )}
          <div className="form__form-group">
            <span className="form__form-group-label">Budget Group State</span>
            <div className="form__form-group-field">
              <Field
                name="state"
                type="text"
                component={renderSelectField}
                options={stateOptions}
              />
            </div>
          </div>
          <ButtonToolbar className="form__button-toolbar">
            <Button color="primary" type="submit" disabled={disableButton}>
              {!(currentEditRow && currentEditRow.budget_group_daily_id_csv) ? 'Add' : 'Edit'}
            </Button>
            <Button type="button" onClick={() => setShowEditModal(false)}>Cancel</Button>
          </ButtonToolbar>
        </form>
      )}
      {!budgetAccountData.cost_metric_view && budgetAccountData.cost_metric_view !== 'roas' && (
        <div>
          <p>Budget Account Settings are required before you can manage Budget Groups</p>
          <br />
          <br />
          <Button type="button" onClick={changeShowEditModal}>Ok</Button>
        </div>
      )}
    </>
  )
};

ItemEditModalForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  currentEditRow: PropTypes.shape(),
  changeShowEditModal: PropTypes.func.isRequired,
  activeDayOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  monthOptions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })).isRequired,
  user: PropTypes.shape().isRequired,
};

ItemEditModalForm.defaultProps = {
  currentEditRow: null,
};

const validate = (values) => {
  const errors = {};
  // All fields are requried!

  // *Budget Group Name (Phase 1)
  // values.budgetGroupName = values.budgetGroupName?.replace(/[^\-0-9A-Za-z ]/, '');
  if (!values.budgetGroupName) {
    errors.budgetGroupName = 'Name field cannot be empty';
  } else if (values && values.budgetGroupName.length > 255) {
    errors.budgetGroupName = 'Max length is 255 characters';
  } else if (values.budgetGroupName.replace(/[^\-0-9A-Za-z ]/, '').length !== values.budgetGroupName.length) {
    errors.budgetGroupName = 'Only alphanumeric, spaces, and hyphons charactors are allowed';
  }
  // + add no special charactors A-Za-z0-9 and -
  // + add no dups
  // TOOL TIP: Case Sensitive

  // *Allowed Days of the Week.
  // + min of one day required
  // UPDATE: Let's do Monday to Sunday
  if (!values.monday && values.tuesday && values.wednesday && values.thursday && values.friday && values.saturday && values.sunday) {
    errors.daysOfTheWeek = 'At least one day of the week needs to be selected';
  }

  // *Portfolios
  // + Load the Account Default  (low prirority)

  // *Includes
  // - only accept 'Limitless Ads' at this stage
  if (values.includes?.value === '' && !values.agencyInternal?.value) {
    errors.includes = 'Includes only allows for Limitless ADs';
  }

  // Validate brand budget fields
  if (values.budgetType?.value === 'brand') {
    if (!values.brand) {
      errors.brand = 'Brand is required for brand budget type';
    }
    if (!values.brandAdFormat) {
      errors.brandAdFormat = 'Ad Format is required for brand budget type';
    }
  }

  // Budget allocation validation - only for item type (not brand)
  // Note: Visual indicator already shows when total doesn't equal 100%
  // No need to add a separate error message here

  // *Cost Target - Always ROAS
  // Validate ROAS values (between 0.333 and 20)
  if (values.costTargetType?.value === 'blended' && (values.costTarget < 0.333 || values.costTarget > 20)) {
    errors.costTarget = 'Cost Target for ROAS must be between 0.333 and 20';
  }
  if (values.costTargetType?.value === 'bnb' && (values.costTargetBrand < 0.333 || values.costTargetBrand > 20)) {
    errors.costTargetBrand = 'Brand Cost Target for ROAS must be between 0.333 and 20';
  }
  if (values.costTargetType?.value === 'bnb' && (values.costTargetNonBrand < 0.333 || values.costTargetNonBrand > 20)) {
    errors.costTargetNonBrand = 'Non-Brand Cost Target for ROAS must be between 0.333 and 20';
  }

  // *Master Item List - Validate ON and OFF lists
  // Cannot have OFF items when split items is checked
  if (values.splitItemsCheckbox && values.masterItemOffList && values.masterItemOffList.trim()) {
    errors.masterItemOffList = 'Cannot have OFF items when "Split Items Into Individual Budget Groups" is checked';
  }

  // Check for duplicates between ON and OFF lists
  if (values.masterItemOnList && values.masterItemOffList) {
    const onItems = values.masterItemOnList.split(',').map(item => item.trim().toUpperCase()).filter(item => item);
    const offItems = values.masterItemOffList.split(',').map(item => item.trim().toUpperCase()).filter(item => item);
    const duplicates = onItems.filter(item => offItems.includes(item));

    if (duplicates.length > 0) {
      errors.masterItemOnList = `Items cannot be in both ON and OFF lists: ${duplicates.join(', ')}`;
      errors.masterItemOffList = `Items cannot be in both ON and OFF lists: ${duplicates.join(', ')}`;
    }
  }

  // Validate ON list items if provided
  if (values.masterItemOnList) {
    const itemList = values.masterItemOnList.split(',').map(item => item.trim()).filter(item => item);
    const retailerKey = values.retailerKey || 'amazon';
    const isBrandBudgetType = values.budgetType?.value === 'brand';

    if (retailerKey === 'amazon') {
      // For Brand budget type, ALL items must be valid ASINs (regardless of seller_account_type_id)
      if (isBrandBudgetType) {
        const invalidItems = itemList.filter(item => !item?.toUpperCase().startsWith('B0') || item?.length !== 10);

        if (invalidItems.length > 0) {
          errors.masterItemOnList = `Brand budgets only accept ASINs (must start with B0 and be 10 characters). Invalid items: ${invalidItems.join(', ')}`;
        } else {
          // Check ASIN count for brand budgets (3-100 required)
          const asinCount = itemList.length;
          if (asinCount < 3) {
            errors.masterItemOnList = `Brand budgets require at least 3 ASINs (currently: ${asinCount})`;
          } else if (asinCount > 100) {
            errors.masterItemOnList = `Brand budgets cannot exceed 100 ASINs (currently: ${asinCount})`;
          }
        }
      } else if (!isBrandBudgetType) {
        // For Item budget type, use the normal validation based on accountType
        const hasB0Items = itemList.some(item => item?.toUpperCase().startsWith('B0') && item?.length === 10);

        if (hasB0Items && values.accountType === 1) {
          errors.masterItemOnList = 'Amazon Seller SKUs cannot start with B0';
        }
        if (!hasB0Items && values.accountType === 2) {
          errors.masterItemOnList = 'Amazon Vendor ASINs must start with B0';
        }
      }
    } else if (retailerKey === 'walmart') {
      let hasNonNumeric = false;
      itemList.forEach(item => {
        const trimmedItem = item?.trim();
        if (trimmedItem && !/^\d+$/.test(trimmedItem)) {
          hasNonNumeric = true;
        }
      });
      if (hasNonNumeric) {
        errors.masterItemOnList = 'Walmart items must be numeric only';
      }
    }
  }

  // Validate OFF list items if provided
  if (values.masterItemOffList) {
    const itemList = values.masterItemOffList.split(',').map(item => item.trim()).filter(item => item);
    const retailerKey = values.retailerKey || 'amazon';
    const isBrandBudgetType = values.budgetType?.value === 'brand';

    if (retailerKey === 'amazon') {
      // For Brand budget type, ALL items must be valid ASINs (but OFF list shouldn't be used for brand anyway)
      if (isBrandBudgetType) {
        const invalidItems = itemList.filter(item => !item?.toUpperCase().startsWith('B0') || item?.length !== 10);

        if (invalidItems.length > 0) {
          errors.masterItemOffList = `Brand budgets only accept ASINs (must start with B0 and be 10 characters). Invalid items: ${invalidItems.join(', ')}`;
        }
      } else if (!isBrandBudgetType) {
        // For Item budget type, use the normal validation based on accountType
        const hasB0Items = itemList.some(item => item?.toUpperCase().startsWith('B0') && item?.length === 10);

        if (hasB0Items && values.accountType === 1) {
          errors.masterItemOffList = 'Amazon Seller SKUs cannot start with B0';
        }
        if (!hasB0Items && values.accountType === 2) {
          errors.masterItemOffList = 'Amazon Vendor ASINs must start with B0';
        }
      }
    } else if (retailerKey === 'walmart') {
      let hasNonNumeric = false;
      itemList.forEach(item => {
        const trimmedItem = item?.trim();
        if (trimmedItem && !/^\d+$/.test(trimmedItem)) {
          hasNonNumeric = true;
        }
      });
      if (hasNonNumeric) {
        errors.masterItemOffList = 'Walmart items must be numeric only';
      }
    }
  }

  // *Dates
  // + New BG, start date must be in the future
  // + Existing BG, you can't edit the start date once it's past that date.
  // + End date cannnot be before or equal the start date.
  // + Date range mush incluide at least one active day (in the future).
  // + What timezone??????
  // Check if this is a new budget group (no budgetGroupRecords with IDs > 0)
  const hasExistingRecords = values.budgetGroupRecords && values.budgetGroupRecords.some(r => r.budget_group_daily_id > 0);
  const isNewBudgetGroup = !hasExistingRecords;
  if (isNewBudgetGroup) {
    if (moment(values.startDate) <= moment()) {
      errors.startDate = 'Start Date must be in the future';
    }
  }
  if (values.endDate) {
    if (moment(values.startDate) >= moment(values.endDate)) {
      errors.endDate = 'End Date must be after Start Date';
    }
  }

  // *Amounts (daily/montly)
  // + Add local currency

  // *Monthly Budget Amount (Phase 3)
  // + noof active day count for each month.
  // + calculate avd using above
  // + confirm we have a min of $1 per item per day
  // + All Months must be filled out (Phase 1)
  if (values.budgetDriver?.value === 'monthly') {
    let totalMonthlyBudget = 0;
    values.monthOptions.forEach(month => {
      const monthValue = values[month.name];
      // Each month must be set (not null/undefined) and cannot be negative
      if (monthValue === null || monthValue === undefined || monthValue === '' || monthValue < 0) {
        errors[month.name] = 'Month budget must be set and cannot be negative';
      } else {
        totalMonthlyBudget += parseFloat(monthValue || 0);
      }
    });

    // At least one month must have a budget greater than 0
    if (totalMonthlyBudget <= 0) {
      errors.monthlyBudgetTotal = 'At least one month must have a budget greater than 0';
    }
  }
  if (values.budgetDriver?.value === 'daily') {
    const hasExistingRecordsForBudget = values.budgetGroupRecords && values.budgetGroupRecords.some(r => r.budget_group_daily_id > 0);
    if (hasExistingRecordsForBudget) {
      // For updates, allow 0 but not negative or empty
      if (values.baseDailyBudgetAmount === null || values.baseDailyBudgetAmount === undefined || values.baseDailyBudgetAmount === '' || values.baseDailyBudgetAmount < 0) {
        errors.baseDailyBudgetAmount = 'Daily budget cannot be negative';
      }
    } else if (!values.baseDailyBudgetAmount || values.baseDailyBudgetAmount <= 0) {
      // For new budget groups, require a positive value
      errors.baseDailyBudgetAmount = 'Daily budget must be set and greater than 0';
    }
  }


  // If account metric doesn't match Group metric, make sure they change the value.

  return errors;
};

export default reduxForm({
  form: 'budgetGroupEditModal',
  validate,
})(ItemEditModalForm);
